import React, { useState, useEffect } from 'react'; 
import { Trash2, ArrowDown, ArrowUp, Calendar, Clock, User, Film, RefreshCw, Share2, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

import './PlaylistOverlay.css';

const PlaylistOverlay = ({ 
  isVisible, 
  onClose, 
  tracks,
  currentTrackIndex, 
  onSelectTrack, 
  onDeleteTrack, 
  onToggleVisibility, // 새로 추가된 prop
  userEmail,
  language,
  isFullScreen,
  SERVER_ROOT // 서버 루트 URL 추가
}) => {
  // 닫힘 애니메이션을 위한 상태 추가
  const [isClosing, setIsClosing] = useState(false);
  // 정렬 관련 상태 추가
  const [sortBy, setSortBy] = useState('addedTime');
  const [sortDirection, setSortDirection] = useState('desc');
  // 정렬된 트랙 목록
  const [sortedTracks, setSortedTracks] = useState([]);
  // 업데이트 중인 트랙 ID를 저장하는 상태
  const [updatingTrackIds, setUpdatingTrackIds] = useState([]);
  
  // 트랙 목록이 변경되거나 정렬 조건이 변경될 때 정렬 수행
  // 트랙 목록이 변경되거나 정렬 조건이 변경될 때 정렬 수행
  useEffect(() => {
    if (!tracks || !tracks.length) return;
    
    const sorted = [...tracks].sort((a, b) => {
      let compareA, compareB;
      
      // 정렬 기준에 따라 비교할 값 설정
      switch (sortBy) {
        case 'channelName':
          compareA = a.channelName || '';
          compareB = b.channelName || '';
          break;
        case 'uploadDate':
          // 날짜 형식의 문자열을 Date 객체로 변환하여 비교
          compareA = a.uploadDate ? new Date(a.uploadDate) : new Date(0);
          compareB = b.uploadDate ? new Date(b.uploadDate) : new Date(0);
          break;
        case 'addedTime':
          // 트랙 추가 시간을 Date 객체로 변환하여 비교
          compareA = a.addedTime ? new Date(a.addedTime) : new Date(0);
          compareB = b.addedTime ? new Date(b.addedTime) : new Date(0);
          break;
        case 'title':
        default:
          compareA = a.videoTitle || '';
          compareB = b.videoTitle || '';
          break;
      }
      
      // 문자열 비교
      if (typeof compareA === 'string' && typeof compareB === 'string') {
        return sortDirection === 'asc' 
          ? compareA.localeCompare(compareB) 
          : compareB.localeCompare(compareA);
      }
      
      // 날짜 비교
      return sortDirection === 'asc' 
        ? compareA - compareB 
        : compareB - compareA;
    });
    
    setSortedTracks(sorted);
  }, [tracks, sortBy, sortDirection]);
  
  // 플레이리스트가 표시되지 않으면 null 반환
  if (!isVisible && !isClosing) return null;
  
  // 닫기 버튼 핸들러
  const handleClose = () => {
    // 닫힘 애니메이션 시작
    setIsClosing(true);
    
    // 애니메이션 시간(300ms) 후 실제로 닫기
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };
  
  // 정렬 기준 변경 핸들러
  const handleSortChange = (criterion) => {
    if (sortBy === criterion) {
      // 같은 기준이면 방향만 변경
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // 다른 기준이면 기본 오름차순 정렬
      setSortBy(criterion);
      setSortDirection('asc');
    }
  };

  // 특정 트랙 정보 업데이트 핸들러
  const handleUpdateTrack = async (trackId, videoUrl) => {
    if (!trackId || !videoUrl || !SERVER_ROOT) return;
    
    // 이미 업데이트 중인 트랙은 다시 업데이트하지 않음
    if (updatingTrackIds.includes(trackId)) return;
    
    // 업데이트 중인 트랙 ID 추가
    setUpdatingTrackIds(prev => [...prev, trackId]);
    
    try {
      // 비디오 ID 추출
      const videoId = extractYouTubeId(videoUrl);
      if (!videoId) throw new Error('Invalid YouTube URL');
      
      // 비디오 정보 가져오기
      const videoInfoResponse = await axios.get(`${SERVER_ROOT}/api/youtube/video-info/${videoId}`);
      
      if (videoInfoResponse.data) {
        const { title, channelName, uploadDate, thumbnail } = videoInfoResponse.data;
        
        // 트랙 정보 업데이트
        await axios.put(`${SERVER_ROOT}/api/youtubePlay/${trackId}`, {
          userEmail,
          videoTitle: title,
          channelName,
          uploadDate,
          thumbnail,
          albumName: tracks.find(t => t.id === trackId)?.albumName
        });
        
        alert('트랙 정보가 업데이트되었습니다.');
        
        // 필요한 경우 페이지 새로고침 또는 트랙 목록 다시 로드
        window.location.reload();
      }
    } catch (error) {
      console.error('트랙 업데이트 오류:', error);
      alert('트랙 정보 업데이트 중 오류가 발생했습니다.');
    } finally {
      // 업데이트 중인 트랙 ID 제거
      setUpdatingTrackIds(prev => prev.filter(id => id !== trackId));
    }
  };
  
  // 모든 표시된 트랙 정보 업데이트 핸들러
  const handleUpdateAllTracks = async () => {
    if (!sortedTracks.length || !SERVER_ROOT) return;
    
    const shouldUpdate = window.confirm(`표시된 트랙 ${sortedTracks.length}개의 정보를 업데이트하시겠습니까?`);
    if (!shouldUpdate) return;
    
    // 업데이트 중인 트랙 ID 추가
    setUpdatingTrackIds(sortedTracks.map(track => track.id));
    
    let updatedCount = 0;
    let errorCount = 0;
    
    for (const track of sortedTracks) {
      try {
        // 비디오 ID 추출
        const videoId = extractYouTubeId(track.url);
        if (!videoId) continue;
        
        // 비디오 정보 가져오기
        const videoInfoResponse = await axios.get(`${SERVER_ROOT}/api/youtube/video-info/${videoId}`);
        
        if (videoInfoResponse.data) {
          const { title, channelName, uploadDate, thumbnail } = videoInfoResponse.data;
          
          // 트랙 정보 업데이트
          await axios.put(`${SERVER_ROOT}/api/youtubePlay/${track.id}`, {
            userEmail,
            videoTitle: title || track.videoTitle,
            channelName,
            uploadDate,
            thumbnail,
            albumName: track.albumName
          });
          
          updatedCount++;
        }
      } catch (error) {
        console.error(`트랙 ID ${track.id} 업데이트 오류:`, error);
        errorCount++;
      }
    }
    
    alert(`총 ${sortedTracks.length}개 중 ${updatedCount}개 트랙이 업데이트되었습니다. ${errorCount}개 실패.`);
    
    // 업데이트 중인 트랙 ID 제거
    setUpdatingTrackIds([]);
    
    // 필요한 경우 페이지 새로고침 또는 트랙 목록 다시 로드
    window.location.reload();
  };
  
  // YouTube ID 추출 함수
  const extractYouTubeId = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|shorts\/|live\/|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11) ? match[2] : null;
  };

  // 맞춤형 클래스 이름 생성
  const overlayClassName = `playlist-overlay ${isFullScreen ? 'fullscreen-mode' : 'normal-mode'}`;
  const menuClassName = `playlist-menu ${isFullScreen ? 'fullscreen-menu' : 'normal-mode'}`;

  // console.log(`menuClassName: ${menuClassName}`);

  // 날짜 포맷팅 함수
  const formatDate = (dateString) => {
    if (!dateString) return '날짜 정보 없음';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(language === 'KR' ? 'ko-KR' : 'en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return '날짜 형식 오류';
    }
  }; 

  // 트랙 공개/비공개 전환 핸들러
  const handleToggleVisibility = async (trackId, newIsPublic) => {
    if (!trackId || !userEmail) return;
    
    try {
      await axios.put(`${SERVER_ROOT}/api/youtubePlay/toggle-public/${trackId}`, {
        userEmail,
        isPublic: newIsPublic
      });
      
      // 로컬 상태 업데이트
      setSortedTracks(prev => 
        prev.map(t => t.id === trackId ? { ...t, isPublic: newIsPublic } : t)
      );
      
      // 부모 컴포넌트에게 상태 변경 알림
      if (typeof onToggleVisibility === 'function') {
        onToggleVisibility(trackId, newIsPublic);
      }
      
      // 알림 메시지 (필요시에만 사용)
      // alert(newIsPublic ? '트랙이 공개로 설정되었습니다.' : '트랙이 비공개로 설정되었습니다.');
    } catch (error) {
      console.error('트랙 공개 상태 변경 오류:', error);
      alert('트랙 공개 상태 변경 중 오류가 발생했습니다.');
    }
  };

  return (
    <div className={overlayClassName}>
      <div className={menuClassName}>
        <div className="playlist-header">
          <h4>{language !== 'KR' ? 'Playlist' : '플레이리스트'} ({tracks.length}곡)</h4>
          <div className="sort-options">
            <button 
              className={`sort-button ${sortBy === 'title' ? 'active' : ''}`}
              onClick={() => handleSortChange('title')}
              title={language !== 'KR' ? 'Sort by title' : '제목으로 정렬'}
            >
              <Film size={16} />
              {sortBy === 'title' && (
                sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
              )}
            </button>
            <button 
              className={`sort-button ${sortBy === 'channelName' ? 'active' : ''}`}
              onClick={() => handleSortChange('channelName')}
              title={language !== 'KR' ? 'Sort by channel' : '채널로 정렬'}
            >
              <User size={16} />
              {sortBy === 'channelName' && (
                sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
              )}
            </button>
            <button 
              className={`sort-button ${sortBy === 'uploadDate' ? 'active' : ''}`}
              onClick={() => handleSortChange('uploadDate')}
              title={language !== 'KR' ? 'Sort by date' : '날짜로 정렬'}
            >
              <Calendar size={16} />
              {sortBy === 'uploadDate' && (
                sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
              )}
            </button>
            <button 
              className={`sort-button ${sortBy === 'addedTime' ? 'active' : ''}`}
              onClick={() => handleSortChange('addedTime')}
              title={language !== 'KR' ? 'Sort by added time' : '추가된 시간순 정렬'}
            >
              <Clock size={16} />
              {sortBy === 'addedTime' && (
                sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
              )}
            </button>
            
            {/* 모든 트랙 정보 업데이트 버튼 추가 */}
            {userEmail === "gategil1149@gmail.com" && (
            <button 
              className={`update-button ${updatingTrackIds.length > 0 ? 'updating' : ''}`}
              onClick={handleUpdateAllTracks}
              disabled={updatingTrackIds.length > 0}
              title={language !== 'KR' ? 'Update all track information' : '모든 트랙 정보 업데이트'}
            >
              <RefreshCw size={16} className={updatingTrackIds.length > 0 ? 'spin' : ''} />
            </button>
            )}
          </div>
          <button className="close-playlist-button" onClick={handleClose}>×</button>
        </div>
        <ul className="playlist-tracks">
          {sortedTracks.map((track, index) => {
            // 현재 트랙의 원래 인덱스 찾기
            const originalIndex = tracks.findIndex(t => t.id === track.id);
            const isUpdating = updatingTrackIds.includes(track.id);
            
            return (
              <li 
                key={track.id} 
                className={`${originalIndex === currentTrackIndex ? 'active-track' : ''} ${isUpdating ? 'updating-track' : ''}`}
                onClick={() => onSelectTrack(originalIndex)}
              > 
                {/* {track.thumbnail && userEmail === "gategil1149@gmail.com" && (
                  <div className="track-thumbnail">
                    <img 
                      src={track.thumbnail} 
                      alt={track.videoTitle || '비디오 썸네일'} 
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `${SERVER_ROOT}/images/thumbnail_default.png`;
                      }}
                    />
                  </div>
                )} */} 
                <div className="track-thumbnail">
                  <img 
                    src={(() => {
                      const videoId = extractYouTubeId(track.url);
                      console.log(`videoId--: ${videoId}`);
                      return videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : `${SERVER_ROOT}/images/thumbnail_default.png`;
                    })()} 
                    alt={track.videoTitle || '비디오 썸네일'} 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = `${SERVER_ROOT}/images/thumbnail_default.png`;
                    }}
                  />
                </div> 
                <div className="track-info-container"> 
                  <div className="track-details">
                    <span className="channel-name">
                      {track.channelName || '채널 정보 없음'}
                    </span>
                    <span className="upload-date">
                      {formatDate(track.uploadDate)}
                    </span>
                    <span className="album-name">
                      {track.albumName || '기본 앨범'}
                    </span>
                  </div>
                  <div className="track-title">
                    {track.videoTitle || '알 수 없는 영상'}
                  </div>
                </div>
                 
                <div className="track-actions">
                  {/* 공유 버튼 추가 */}
                  <button 
                    onClick={(e) => {
                      // handleShareTrack(e, track);
                      e.stopPropagation();
                      // 현재 URL에 trackId 파라미터 추가하여 클립보드에 복사
                      const videoId = extractYouTubeId(track.url);
                      // 개발환경인지 프로덕션 환경인지 확인하여 적절한 도메인 사용
                      const domain = process.env.NODE_ENV === 'production' 
                        ? 'https://www.doldari.com' 
                        : window.location.origin;
                      const shareUrl = `https://www.doldari.com:3001/share/youtube?trackId=${track.id}&video=${videoId}&t=${Date.now()}`;
                      console.log(`shareUrl2: ${shareUrl}`);

                      navigator.clipboard.writeText(shareUrl)
                        .then(() => {
                          alert(language !== 'KR' ? 'Track link copied to clipboard!' : '트랙 링크가 클립보드에 복사되었습니다!');
                        })
                        .catch(err => {
                          console.error('클립보드 복사 오류:', err);
                          alert(language !== 'KR' ? 'Failed to copy link' : '링크 복사에 실패했습니다');
                        });
                    }}
                    className="share-button"
                    title={language !== 'KR' ? 'Share this track' : '이 트랙 공유하기'}
                  >
                    <Share2 size={14} />
                  </button>

                  {/* 개별 트랙 업데이트 버튼 */}
                  {/* {userEmail === "gategil1149@gmail.com" && (
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateTrack(track.id, track.url);
                    }}
                    className={`update-track-button ${isUpdating ? 'updating' : ''}`}
                    title={language !== 'KR' ? 'Update track info' : '트랙 정보 업데이트'}
                    disabled={isUpdating}
                  >
                    <RefreshCw size={14} className={isUpdating ? 'spin' : ''} />
                  </button>
                  )} */}
                  
                  {/* 공개/비공개 토글 버튼 (사용자가 트랙 소유자인 경우에만 표시) */}
                  {(track.userEmail === userEmail) && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleVisibility(track.id, !track.isPublic);
                      }}
                      className="visibility-button"
                      style={{backgroundColor: 'transparent'}}
                      title={track.isPublic ? (language !== 'KR' ? 'Set to private' : '비공개로 설정') : (language !== 'KR' ? 'Set to public' : '공개로 설정')}
                    >
                      {/* {track.isPublic ? "public" : "private"} */}
                      {track.isPublic ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                  )}

                  {/* 삭제 버튼 */}
                  {(track.userEmail === userEmail || userEmail === "gategil1149@gmail.com") && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteTrack(track.id, originalIndex, e);
                      }}
                      className="delete-button"
                      title="삭제"
                      style={{background: 'none'}}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default PlaylistOverlay;